import { createTheme } from '@nextui-org/react';
import { BaseTheme, Colors } from '@nextui-org/react/types/theme';

import { fonts } from './fonts';
import { AppTheme } from './interfaces';

/**
 * Map our custom {@link AppTheme} interface to a NextUI compatible object.
 * @param theme the application-bound theme.
 * @returns a NextUI Theme
 */
export const getNextUITheme = (theme: AppTheme) => {
  const nextUIColorTokenOverrides = getNextUIColorTokenOverrides(theme);
  const customColorTokens = getCustomColorTokens(theme);
  const nextUIThemeOverridesMap = getNextUIThemeOverridesMap(nextUIColorTokenOverrides, customColorTokens);

  return createTheme({
    type: theme.name.toLowerCase(), // it could be "light" or "dark"
    theme: nextUIThemeOverridesMap,
  });
};

/**
 * Combine all Application Theme overrides into a single object compatible with {@link createTheme}.
 * @param nextUIColorTokenOverrides object containing the overridden Color Tokens for NextUI & Stitches
 * @param customColorTokens object containing the overridden Color Tokens for NextUI & Stitches
 * @returns object containing {@link BaseTheme} overrides to be passed to NextUI & Stitches
 */
const getNextUIThemeOverridesMap = (
  nextUIColorTokenOverrides: Partial<Colors>,
  customColorTokens: BaseTheme['colors'],
): Partial<BaseTheme> => {
  return {
    colors: {
      ...nextUIColorTokenOverrides,
      ...customColorTokens,
    },
    borderWeights: {
      normal: '1px',
    },
    lineHeights: {
      xs2: '1rem',
      xs3: '1.125rem',
      sm2: '1.25rem',
      base2: '1.5rem',
    },
    radii: {
      xxs: '6px',
      xs: '8px',
    },
    space: {
      '6a': '0.8125rem',
      '10a': '1.625rem',
      25: '6.125rem',
      31: '7.875rem',
    },
    fonts,
    fontSizes: {
      checkbox: '$base',
      input: '1rem',
    },
  };
};

/**
 * Given an {@link AppTheme}, returns an object containing NextUI & Stitches {@link Colors} overrides.
 * @param theme the application-bound theme
 * @returns object containing the overridden Color Tokens for NextUI & Stitches
 */
const getNextUIColorTokenOverrides = (theme: AppTheme): Partial<Colors> => {
  return {
    // Primary Brand Colors
    primaryLight: theme.colors.primary.c200,
    primaryLightHover: theme.colors.primary.c300, // commonly used on hover state
    primaryLightActive: theme.colors.primary.c400, // commonly used on pressed state
    primaryLightContrast: theme.colors.primary.c600, // commonly used for text inside the component
    primary: theme.colors.primary.c600,
    primaryBorder: theme.colors.primary.c500,
    primaryBorderHover: theme.colors.primary.c600,
    primarySolidHover: theme.colors.primary.c700,
    primarySolidContrast: theme.colors.greyscale.c050, // commonly used for text inside the component
    primaryShadow: theme.colors.primary.c500,
    gradient: 'linear-gradient(112deg, $blue100 -25%, $pink500 -10%, $purple500 80%)',
    link: theme.colors.primary.c400,
    background: theme.colors.primary.c900,
  };
};

/**
 * Define custom Color tokens for use in NextUI + Stitches
 * @param theme the application-bound theme
 * @returns object containing the overridden Color Tokens for NextUI & Stitches
 */
const getCustomColorTokens = (theme: AppTheme) => {
  return {
    inputBorder: theme.colors.neutrals.c300,
    inputBackground: theme.colors.black,
    text: theme.colors.white,
    // textSecondary: theme.colors.greyscale.c600,
    // grayScaleText: theme.colors.greyscale.c500,
    disabledBackground: theme.colors.greyscale.c800,
  };
};
